/**
 * Tool Service
 *
 * Complete CRUD and operations service for Tool Designer.
 * Provides create, update, get, list, delete, compile, and deploy operations.
 *
 * Host-First Architecture (NEW):
 * - Platform stores REFERENCES only (hostId, schemas, governance)
 * - Host stores SOURCE CODE and BINARIES
 * - Code generation happens at the host via LLM
 * - Use generateAtHost() for new tool creation
 */

import { parseInvocationUsage } from '@/shared/api/invocation-usage'
import { UnifiedApiError } from '@/lib/api-error'
import type {
  ToolDefinition,
  ToolLifecycleStage,
  CreateToolRequest,
  UpdateToolRequest,
  ToolListResponse,
  GenerateAtHostRequest,
  GenerateAtHostResponse,
  HostGenerationStatus,
} from '@/types/tool.types';
import { getConfig } from '@/shared/config/runtime-env';
import { useAuth } from '@/shared/auth/unified-auth';

// Use API gateway URL - routes to governance-service via APISIX
const API_URL = getConfig('VITE_API_URL', 'http://localhost:8082');

/**
 * Mission-scoped tool surface base (ADR-20260610 slice 2, #947).
 * When the owning mission is known, mutations go through
 * /v1/governance/missions/{missionId}/tools; otherwise the legacy
 * org-scoped surface. After cutover commit C the legacy surface answers
 * 409 for mutations, so every mutating caller must supply the mission
 * (a tool's own record carries ownerMissionId).
 */
const toolsBase = (missionId?: string): string =>
  missionId
    ? `${API_URL}/v1/governance/missions/${missionId}/tools`
    : `${API_URL}/v1/governance/tools`;

/**
 * Resolves the mission that owns a tool from its own record, so per-tool
 * mutators route through the mission surface without every component
 * having to thread mission context. Reads stay org-scoped, so this lookup
 * keeps working after the legacy mutation flip. Unowned legacy tools
 * resolve to undefined and fall back to the legacy surface (read-only
 * after cutover commit C — adopting into a mission is the path forward).
 */
async function resolveOwnerMission(toolId: string, explicit?: string): Promise<string | undefined> {
  if (explicit) return explicit;
  // Platform tools use name-ids (no governance row, ownerless by design);
  // only UUID ids can resolve to an owned generated tool.
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(toolId)) return undefined;
  try {
    const tool = await getTool(toolId);
    return tool.ownerMissionId || undefined;
  } catch {
    return undefined;
  }
}

/**
 * Get the organization ID from Zustand auth store for multi-tenant API requests.
 * SECURITY: Never read from localStorage — XSS could forge org context.
 */
function getOrganizationId(): string | null {
  return useAuth.getState()?.user?.organizationId ?? null;
}

/**
 * Get headers for API requests including organization context.
 */
function getHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const orgId = getOrganizationId();
  if (orgId) {
    (headers as Record<string, string>)['X-Organization-ID'] = orgId;
  }

  return headers;
}

/**
 * Extract error message from API response.
 * Handles both camelCase (frontend) and PascalCase (backend) field names.
 *
 * For 4xx responses we prefer `details` over `message` because the
 * backend's ErrorResponse contract puts the generic UI prose in
 * `message` (e.g. "Failed to initiate generation") and the actionable
 * reason in `details` (e.g. the specific collision text). For 5xx we
 * keep the legacy precedence — `details` there is typically an internal
 * trace, not user-facing. Origin: GitLab #834.
 */
function extractErrorMessage(
  errorData: Record<string, unknown>,
  fallback: string,
  status?: number,
): string {
  const messageish =
    (errorData['message'] as string) ||
    (errorData['Message'] as string) ||
    (errorData['error'] as string) ||
    (errorData['Error'] as string);
  const detailsish =
    (errorData['details'] as string) || (errorData['Details'] as string);

  const is4xx = status !== undefined && status >= 400 && status < 500;
  if (is4xx) {
    return detailsish || messageish || fallback;
  }
  return messageish || detailsish || fallback;
}

/**
 * Error thrown by tool-service calls that exposes the backend's
 * machine-readable `code` field. The admin-ui keys off `code` (not
 * the prose message) when it needs to render an action button — e.g.
 * the wizard renders "Open Model Bindings" when generateAtHost fails
 * with `no_code_generation_model_configured`. Without this subclass
 * the only signal is the prose, which is locale-dependent and brittle.
 */
export class ToolServiceError extends Error {
  readonly code: string | undefined;
  readonly status: number | undefined;
  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message);
    this.name = 'ToolServiceError';
    this.code = opts?.code;
    this.status = opts?.status;
  }
}

function extractErrorCode(errorData: Record<string, unknown>): string | undefined {
  const v = errorData['code'] ?? errorData['Code'];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

// ============================================================================
// Response Types
// ============================================================================

export interface DeployResult {
  success: boolean;
  toolId: string;
  hostId: string;
  deployedAt: string;
  error?: string;
}

export interface ToolFilters {
  includeUsage?: boolean;
  sortBy?: 'name' | 'created_at' | 'status';
  sortOrder?: 'asc' | 'desc';
  category?: string;
  status?: string;
  lifecycleStage?: ToolLifecycleStage;
  hostId?: string;
  ownerMissionId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

// ============================================================================
// Backend → Frontend Mapping
// ============================================================================

/**
 * Maps deprecated lifecycle stage values to current ones.
 * Backend migration V179 handles DB records, but in-flight responses may still
 * contain old values during deployment rollover.
 */
function mapLifecycleStage(stage: string): ToolLifecycleStage {
  switch (stage) {
    case 'compiled': return 'generated';
    case 'compiling': return 'draft';
    // Retired by ADR-20260610 D3 (V310 moved these rows); a legacy value
    // reads as the built stage it was promoted from.
    case 'approved': return 'generated';
    case 'draft':
    case 'generated':
      return stage;
    default:
      return 'draft';
  }
}

/**
 * Maps a backend tool response (snake_case) to frontend ToolDefinition (camelCase).
 * The backend sends flat fields like host_id, lifecycle_stage, created_at, updated_at
 * while the frontend expects camelCase with metadata.createdAt/updatedAt populated.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapToolResponse(raw: any): ToolDefinition {
  const governance = raw.governance || {};
  return {
    ...raw,
    usage: parseInvocationUsage(raw.usage),
    hostId: raw.host_id || raw.hostId || '',
    // displayName: V147+ servers always populate this (with their own
    // beautify(name) fallback). Reader still tolerates absence + the
    // camelCase variant so a stale fixture or pre-V147 cached
    // response doesn't strip the field.
    displayName: raw.display_name ?? raw.displayName ?? undefined,
    // V27 + Sprint A.4: bindings are persisted on the tool. The
    // governance-service's GetTool / getToolSource both include
    // capsule_ids / resource_ids when the row carries them; default
    // [] so consumers can iterate without nil-guarding.
    capsuleIds: Array.isArray(raw.capsule_ids)
      ? raw.capsule_ids
      : Array.isArray(raw.capsuleIds)
        ? raw.capsuleIds
        : [],
    resourceIds: Array.isArray(raw.resource_ids)
      ? raw.resource_ids
      : Array.isArray(raw.resourceIds)
        ? raw.resourceIds
        : [],
    pattern: raw.pattern || raw.tool_pattern || undefined,
    // User attribution. snake_case from V147+ servers; camelCase
    // tolerated for fixtures.
    createdBy: raw.created_by ?? raw.createdBy ?? undefined,
    updatedBy: raw.updated_by ?? raw.updatedBy ?? undefined,
    lifecycleStage: mapLifecycleStage(raw.lifecycle_stage || raw.lifecycleStage || 'draft'),
    ownerMissionId: raw.owner_mission_id || raw.ownerMissionId || undefined,
    compileStatus: raw.compile_status || raw.compileStatus || undefined,
    sourcePaths: Array.isArray(raw.source_paths)
      ? raw.source_paths
      : Array.isArray(raw.sourcePaths)
        ? raw.sourcePaths
        : [],
    hasSource: raw.has_source ?? raw.hasSource ?? false,
    availability: raw.availability
      ? {
          state: raw.availability.state,
          reason: raw.availability.reason,
          hostId: raw.availability.host_id ?? raw.availability.hostId ?? undefined,
          canRepair: raw.availability.can_repair ?? raw.availability.canRepair ?? false,
        }
      : undefined,
    inputSchema: raw.input_schema || raw.inputSchema || { type: 'object', properties: {} },
    outputSchema: raw.output_schema || raw.outputSchema || { type: 'object', properties: {} },
    metadata: {
      createdAt: raw.created_at || raw.metadata?.created_at || raw.metadata?.createdAt || '',
      updatedAt: raw.updated_at || raw.metadata?.updated_at || raw.metadata?.updatedAt || '',
      author: raw.metadata?.author || '',
      description: raw.metadata?.description || raw.description || '',
      tags: Array.isArray(raw.tags) ? raw.tags : Array.isArray(raw.metadata?.tags) ? raw.metadata.tags : [],
      isDraft: raw.metadata?.is_draft ?? raw.metadata?.isDraft ?? false,
      copyProvenance: raw.metadata?.copy_provenance ?? raw.metadata?.copyProvenance ?? undefined,
    },
    governance: {
      reviewerId: governance.reviewer_id || governance.reviewerId || undefined,
      reviewedAt: governance.reviewed_at || governance.reviewedAt || undefined,
      reviewNotes: governance.review_notes || governance.reviewNotes || undefined,
      requiredTrustLevel: governance.required_trust_level || governance.requiredTrustLevel || 'standard',
      submittedBy: governance.submitted_by || governance.submittedBy || undefined,
      submittedAt: governance.submitted_at || governance.submittedAt || undefined,
    },
    nodes: Array.isArray(raw.nodes) ? raw.nodes : [],
    edges: Array.isArray(raw.edges) ? raw.edges : [],
    dataSources: Array.isArray(raw.data_sources || raw.dataSources) ? (raw.data_sources || raw.dataSources) : [],
    testCases: Array.isArray(raw.test_cases || raw.testCases) ? (raw.test_cases || raw.testCases) : [],
  };
}

// ============================================================================
// CRUD Operations
// ============================================================================

/**
 * Create a new tool
 */
export async function createTool(tool: CreateToolRequest, missionId?: string): Promise<ToolDefinition> {
  // Wire format: the Go create handler reads snake_case (`host_id`,
  // `input_schema`, `output_schema`) — see governance-service
  // CreateToolRequest. `JSON.stringify(tool)` previously sent the
  // camelCase request verbatim, so `hostId` never reached the server and
  // create failed with "host_id is required". This path only became
  // reachable once the Mission Designer enabled the manual code-editor
  // flow, which is what surfaced the latent mismatch. Remap explicitly
  // rather than relying on accidental name agreement, mirroring
  // updateTool / generateAtHost. name/description/category/tags match on
  // both sides; nodes/edges are camelCase on both sides.
  const body: Record<string, unknown> = {
    host_id: tool.hostId,
    name: tool.name,
    description: tool.description,
    category: tool.category,
    input_schema: tool.inputSchema,
    output_schema: tool.outputSchema,
  };
  if (tool.tags !== undefined) body.tags = tool.tags;
  if (tool.nodes !== undefined) body.nodes = tool.nodes;
  if (tool.edges !== undefined) body.edges = tool.edges;

  const response = await fetch(toolsBase(missionId), {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to create tool (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * Update an existing tool.
 *
 * Wire format note: the Go side reads snake_case fields (`display_name`,
 * `input_schema`, `output_schema`, `owner_mission_id`). Other multi-word
 * fields the wider request type carries (nodes / edges / dataSources /
 * testCases) historically went through `JSON.stringify` directly because
 * they're camelCase on both sides — we don't change those here. We only
 * remap the snake_case-on-server fields explicitly so we stop relying on
 * accidental name agreement.
 */
export async function updateTool(id: string, tool: UpdateToolRequest, missionId?: string): Promise<ToolDefinition> {
  // Build the wire payload. Only include keys the caller actually
  // set so the server's "field present → update, absent → don't
  // touch" semantics work — JSON.stringify drops `undefined` values
  // anyway, but being explicit here documents the contract.
  const body: Record<string, unknown> = {};
  if (tool.name !== undefined) body.name = tool.name;
  if (tool.displayName !== undefined) body.display_name = tool.displayName;
  if (tool.description !== undefined) body.description = tool.description;
  if (tool.category !== undefined) body.category = tool.category;
  if (tool.inputSchema !== undefined) body.input_schema = tool.inputSchema;
  if (tool.outputSchema !== undefined) body.output_schema = tool.outputSchema;
  if (tool.tags !== undefined) body.tags = tool.tags;
  if (tool.nodes !== undefined) body.nodes = tool.nodes;
  if (tool.edges !== undefined) body.edges = tool.edges;
  if (tool.dataSources !== undefined) body.data_sources = tool.dataSources;
  if (tool.testCases !== undefined) body.test_cases = tool.testCases;
  // Sprint A.5: bindings. Send the explicit array (including empty
  // array = "clear all"). The Go side accepts a *[]string so an
  // empty array clears, undefined = don't touch.
  if (tool.capsuleIds !== undefined) body.capsule_ids = tool.capsuleIds;
  if (tool.resourceIds !== undefined) body.resource_ids = tool.resourceIds;
  if (tool.pattern !== undefined) body.pattern = tool.pattern;

  const owner = await resolveOwnerMission(id, missionId);
  const response = await fetch(`${toolsBase(owner)}/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to update tool (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * Get a tool by ID
 */
export async function getTool(id: string): Promise<ToolDefinition> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${id}`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to fetch tool (HTTP ${response.status})`));
  }

  const data = await response.json();
  return mapToolResponse(data);
}

/**
 * List tools with optional filters
 */
export async function listTools(filters?: ToolFilters): Promise<ToolListResponse> {
  const params = new URLSearchParams();
  if (filters?.includeUsage) params.set('include_usage', 'true');
  if (filters?.sortBy) params.set('sort_by', filters.sortBy);
  if (filters?.sortOrder) params.set('sort_order', filters.sortOrder);

  if (filters?.category) params.set('category', filters.category);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.lifecycleStage) params.set('lifecycle_stage', filters.lifecycleStage);
  if (filters?.hostId) params.set('host_id', filters.hostId);
  if (filters?.ownerMissionId) params.set('owner_mission_id', filters.ownerMissionId);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.limit) params.set('limit', String(filters.limit));
  if (filters?.offset) params.set('offset', String(filters.offset));

  const queryString = params.toString();
  const url = `${API_URL}/v1/governance/tools${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new UnifiedApiError(response.status, extractErrorMessage(errorData, `Failed to list tools (HTTP ${response.status})`), errorData);
  }

  const data = await response.json();
  return {
    ...data,
    tools: Array.isArray(data.tools) ? data.tools.map(mapToolResponse) : [],
  };
}

/**
 * Search tools result returned by POST /v1/governance/tools/search.
 *
 * The backend uses the same scoring algorithm as the orchestration-service's
 * fulfillment fallback (term-coverage in name and description). Confidence is
 * calibrated [0,1] where "all query terms found in name" yields 1.0 — meaning
 * the value is comparable to the confidences emitted by the initial workflow
 * fulfillment pass on a generated workflow. Use this endpoint instead of
 * client-side ranking whenever you can: the result ordering and the auto-bind
 * threshold (0.9) stay consistent across the surfaces.
 */
export interface ToolSearchResult {
  id: string;
  name: string;
  description?: string;
  category?: string;
  /** Calibrated cosine/term-coverage score in [0,1]. */
  confidence: number;
  /** "name" | "description" | "name+description" | "category" | "vector_similarity" */
  matchedOn?: string;
  /** Human-readable reason the backend produced. */
  reasoning?: string;
  /** When host_id is supplied, indicates whether the tool is currently deployed there. */
  available?: boolean;
  warning?: string;
}

export interface SearchToolsRequest {
  query: string;
  /** Default 0.3 server-side. Match the server's auto-bind threshold (0.9) only when you want auto-binding. */
  minConfidence?: number;
  /** Default 20 server-side, capped at 100. */
  maxResults?: number;
  categories?: string[];
  /** When set, results are annotated with `available` for the host. */
  hostId?: string;
  /**
   * Retrieval mode.
   *
   *   "semantic" (default) — POST /v1/governance/tools/semantic-search.
   *     Tries the intelligence-zone vector path first (cosine similarity
   *     over 768d binary-quantized embeddings on the org's tools capsule)
   *     and transparently falls back to keyword scoring when the index
   *     is unavailable. Confidences are calibrated [0,1] in the same
   *     scale workflow generation produces, so the existing
   *     ConfidencePill thresholds and auto-bind threshold (0.9) keep
   *     their meaning.
   *
   *   "keyword" — POST /v1/governance/tools/search. Term-coverage
   *     scoring only. Used when callers explicitly want deterministic
   *     keyword behaviour (e.g. tests, or surfaces matching the
   *     orchestration-service's hot-path scoring exactly).
   *
   * Both modes return the same response shape, so callers can switch
   * without touching their parser.
   */
  mode?: 'semantic' | 'keyword';
}

/**
 * Search the org's tool catalog by natural-language query.
 *
 * Used by the Mission Designer's "Re-suggest tools" affordance to refresh
 * a node's suggestions against the current catalog. Defaults to the
 * semantic endpoint, which is what closes the divergence between
 * re-suggest results and the workflow-generation fulfillment results
 * (both ultimately route through intelligence-zone /api/v1/compose when
 * available).
 *
 * Returns an empty array (not an error) for blank queries — this matches
 * server behaviour and keeps callers from having to guard against it.
 */
export async function searchTools(req: SearchToolsRequest): Promise<ToolSearchResult[]> {
  if (!req.query.trim()) return [];

  const mode = req.mode ?? 'semantic';
  const path = mode === 'keyword'
    ? '/v1/governance/tools/search'
    : '/v1/governance/tools/semantic-search';

  const body = {
    query: req.query,
    min_confidence: req.minConfidence ?? 0.3,
    max_results: req.maxResults ?? 20,
    ...(req.categories && req.categories.length > 0 ? { categories: req.categories } : {}),
    ...(req.hostId ? { host_id: req.hostId } : {}),
  };

  const response = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      extractErrorMessage(errorData, `Failed to search tools (HTTP ${response.status})`),
    );
  }

  const data: {
    results?: Array<{
      tool: { id: string; name: string; category?: string; description?: string };
      confidence: number;
      matched_on?: string;
      reasoning?: string;
      available?: boolean;
      warning?: string;
    }>;
  } = await response.json();

  return (data.results ?? []).map((r) => ({
    id: r.tool.id,
    name: r.tool.name,
    category: r.tool.category,
    description: r.tool.description,
    confidence: r.confidence,
    matchedOn: r.matched_on,
    reasoning: r.reasoning,
    available: r.available,
    warning: r.warning,
  }));
}

/**
 * Bulk delete multiple tools by ID.
 * Returns counts of deleted and any errors.
 */
export async function bulkDeleteTools(ids: string[], missionId?: string): Promise<{ deleted: number; errors: Array<{ tool_id: string; message: string }>; total: number }> {
  const response = await fetch(`${toolsBase(missionId)}/bulk-delete`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ ids }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to bulk delete tools (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * One tool removed by the dead-host force-delete (challenge
 * deleted-host-tool-cleanup.md feature #1). `reason` is the backend's
 * machine-readable classification: `"dead_host"` (the owning host row
 * is gone) or `"orphan_mission"` (the owner mission no longer exists).
 */
export interface ForceDeletedTool {
  toolId: string
  name: string
  reason: string
}

/**
 * Outcome of DELETE /v1/governance/tools/by-host/{host_id}.
 *
 * `refused` counts tools the backend left untouched because they are
 * still functional (live host AND live owner mission) — those have a
 * working delete path through the mission surface and must never be
 * purged here. `errors` carries the same {tool_id, message} shape as
 * bulkDeleteTools so callers can reuse error rendering.
 */
export interface ForceDeleteDeadHostResult {
  deleted: ForceDeletedTool[]
  refused: number
  errors: Array<{ tool_id: string; message: string }>
}

/**
 * Force-delete dead-host / orphan-mission tools pinned to a host
 * (admin-only; the route is RequireAdmin server-side).
 *
 * Hits the ORG-scoped surface `${toolsBase()}/by-host/{host_id}` — NOT
 * the mission-scoped base — because these tools have no reachable owner
 * mission to delete through (that is exactly why they get stuck in the
 * catalog). The LegacyToolMutationGuard whitelists DELETE by-host past
 * the read-only flip. The backend's per-tool predicate refuses any
 * still-functional tool, so this is safe to call for a whole host.
 */
export async function forceDeleteDeadHostTools(hostId: string): Promise<ForceDeleteDeadHostResult> {
  const response = await fetch(`${toolsBase()}/by-host/${hostId}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(extractErrorMessage(errorData, `Failed to remove dead-host tools (HTTP ${response.status})`))
  }

  // Backend wire format is snake_case (deleted[].tool_id, refused,
  // errors[].tool_id); convert at this adapter boundary so callers see
  // camelCase on the deleted entries. errors keep their snake_case
  // tool_id to match the existing bulkDeleteTools error contract.
  const data = await response.json()
  return {
    deleted: Array.isArray(data.deleted)
      ? data.deleted.map((d: { tool_id: string; name: string; reason: string }) => ({
          toolId: d.tool_id,
          name: d.name,
          reason: d.reason,
        }))
      : [],
    refused: typeof data.refused === 'number' ? data.refused : 0,
    errors: Array.isArray(data.errors) ? data.errors : [],
  }
}

/**
 * Delete a tool
 */
export async function deleteTool(id: string, missionId?: string): Promise<void> {
  const owner = await resolveOwnerMission(id, missionId);
  const response = await fetch(`${toolsBase(owner)}/${id}`, {
    method: 'DELETE',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to delete tool (HTTP ${response.status})`));
  }
}

// ============================================================================
// Compile Operations
// ============================================================================

/**
 * Compile tool to validate Go code
 */
export async function compileTool(id: string): Promise<{ success: boolean; errors?: string[] }> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${id}/compile`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Compilation failed (HTTP ${response.status})`));
  }

  return response.json();
}

// ============================================================================
// Deploy Operations
// ============================================================================

/**
 * Deploy tool to a BYOH host
 */
export async function deployTool(id: string, hostId: string): Promise<DeployResult> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${id}/deploy`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ hostId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Deployment failed (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * Sync tool to a BYOH host (without full deployment)
 */
export async function syncToolToHost(id: string, hostId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${id}/sync`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ hostId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Sync failed (HTTP ${response.status})`));
  }

  return response.json();
}

// ============================================================================
// Governance Operations
// ============================================================================

/**
 * Submit tool for governance review
 */
export async function submitForReview(id: string, notes?: string): Promise<ToolDefinition> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${id}/submit-review`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ notes }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to submit for review (HTTP ${response.status})`));
  }

  return response.json();
}

// ============================================================================
// Tool Lifecycle
// ============================================================================
// Lifecycle: draft <-> generated; the mission publish is the approval
// (ADR-20260610 D3).

/**
 * Submit a tool for compilation at its bound host.
 * @deprecated Generation now happens inline via ToolStudio. Use generateAtHost() instead.
 */
export async function submitForCompilation(id: string, missionId?: string): Promise<ToolDefinition> {
  const response = await fetch(`${toolsBase(await resolveOwnerMission(id, missionId))}/${id}/submit-compile`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to submit for compilation (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * Clone a tool for use in another mission.
 * Creates a copy owned by the target mission.
 */
export async function cloneToolForMission(
  sourceToolId: string,
  targetMissionId: string
): Promise<ToolDefinition> {
  // Adoption-by-copy on the target mission's surface (ADR-20260610): the
  // collection POST with source_tool_id clones the source into the target.
  const response = await fetch(`${toolsBase(targetMissionId)}/`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ source_tool_id: sourceToolId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to clone tool for mission (HTTP ${response.status})`));
  }

  const data = await response.json();
  return mapToolResponse(data);
}

// ============================================================================
// Compile Status (Phase 1: Auto-Compilation Pipeline)
// ============================================================================

export type CompileStatus = 'pending' | 'queued' | 'compiling' | 'success' | 'failed';

export interface CompileStatusUpdate {
  tool_id: string;
  host_id?: string;
  status: CompileStatus;
  message?: string;
  error?: string;
  binary_hash?: string;
  compile_time_ms?: number;
  timestamp: string;
  hosts_ready?: number;
  total_hosts?: number;
}

/**
 * Get current compile status for a tool
 */
export async function getCompileStatus(toolId: string): Promise<CompileStatusUpdate> {
  const response = await fetch(
    `${API_URL}/v1/governance/tools/${toolId}/compile-status`,
    {
      method: 'GET',
      headers: getHeaders(),
      credentials: 'include',
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to get compile status (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * Trigger auto-compilation for a tool with source code
 */
export async function triggerAutoCompile(
  toolId: string,
  sourceCode: string
): Promise<{ status: string; tool_id: string; message: string }> {
  const response = await fetch(
    `${API_URL}/v1/governance/tools/${toolId}/auto-compile`,
    {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({ source_code: sourceCode }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to trigger compilation (HTTP ${response.status})`));
  }

  return response.json();
}

/**
 * Subscribe to compile status updates via SSE
 * Returns an EventSource that can be closed when done
 */
export function subscribeToCompileStatus(
  toolId: string,
  onUpdate: (update: CompileStatusUpdate) => void,
  onError?: (error: Event) => void
): EventSource {
  const url = new URL(`${API_URL}/v1/governance/tools/${toolId}/compile-status/stream`);

  const eventSource = new EventSource(url.toString(), {
    withCredentials: true,
  });

  eventSource.addEventListener('compile', (event) => {
    try {
      const update: CompileStatusUpdate = JSON.parse((event as MessageEvent).data);
      onUpdate(update);
    } catch (e) {
      console.error('Failed to parse compile status update:', e);
    }
  });

  eventSource.addEventListener('connected', () => {
    console.log(`Connected to compile status stream for tool ${toolId}`);
  });

  if (onError) {
    eventSource.onerror = onError;
  }

  return eventSource;
}

// ============================================================================
// Tool Source Read (Slice 15b)
// ============================================================================

/**
 * Tool source code as returned by the agent-host (relayed via
 * governance-service over the VPN gateway). Source code lives only on the
 * host per the host-first architecture; the platform proxies on demand.
 */
/**
 * One entry in a multi-file tool's source tree (V26 / Slice 19).
 * Path is relative to the tool root (`main.go`, `pkg/util/io.go`).
 * Single-file tools have a one-element array containing main.go.
 */
export interface ToolSourceFile {
  path: string;
  content: string;
}

export interface ToolSource {
  toolId: string;
  platformResourceId: string;
  name: string;
  version: string;
  language: string;
  /** Legacy single-file fast path; mirrors files[main.go].content. */
  sourceCode: string;
  sourceHash: string;
  /** Canonical multi-file representation (V26). Always non-empty. */
  sourceFiles: ToolSourceFile[];
  isTemplateSource?: boolean;
  compileStatus: string;
  compileError?: string;
  /**
   * V27: persisted tool resource bindings. Always present (defaults
   * to []) so callers don't need missing-vs-empty disambiguation.
   * Empty arrays mean "this tool was authored without bindings"
   * (a pure-compute tool that operates on its inputs alone).
   */
  capsuleIds: string[];
  resourceIds: string[];
  updatedAt: string;
}

/**
 * Read the Go source for a tool.
 *
 * Hits GET /v1/governance/tools/{id}/source which proxies to the
 * agent-host's /v1/tools/{tool_id}/source. The platform resolves the bound
 * host from the platform tool record and forwards over the VPN gateway.
 *
 * Used by the Tool Studio editor on ToolDetailPage (Slice 16, Monaco).
 */
export async function getToolSource(toolId: string): Promise<ToolSource> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${toolId}/source`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 404 || response.status === 502) {
      try {
        const tool = await getTool(toolId);
        if (!tool.ownerMissionId) {
          return templateToolSource(tool);
        }
      } catch {
        // Fall through to the original source error.
      }
    }
    throw new Error(extractErrorMessage(errorData, `Failed to load source (HTTP ${response.status})`));
  }
  const data = await response.json();
  // V26 backfill: hosts that haven't migrated yet may return source_files
  // as null/undefined — wrap source_code into a single-element array so
  // the UI never has to special-case a missing files[].
  const sourceFiles: ToolSourceFile[] = Array.isArray(data.source_files) && data.source_files.length > 0
    ? data.source_files.map((f: { path: string; content: string }) => ({ path: f.path, content: f.content }))
    : [{ path: 'main.go', content: data.source_code || '' }];
  return {
    toolId: data.tool_id,
    platformResourceId: data.platform_resource_id,
    name: data.name,
    version: data.version,
    language: data.language,
    sourceCode: data.source_code,
    sourceHash: data.source_hash,
    sourceFiles,
    isTemplateSource: data.is_template_source === true,
    compileStatus: data.compile_status,
    compileError: data.compile_error || undefined,
    // V27: pre-V27 hosts return undefined for these fields; default
    // to [] so the panel doesn't have to disambiguate. Both legitimate
    // empty arrays from V27+ hosts and missing fields from pre-V27
    // hosts surface as the same "no bindings" state.
    capsuleIds: Array.isArray(data.capsule_ids) ? data.capsule_ids : [],
    resourceIds: Array.isArray(data.resource_ids) ? data.resource_ids : [],
    updatedAt: data.updated_at,
  };
}

function templateToolSource(tool: ToolDefinition): ToolSource {
  const sourceCode = templateToolSourceCode();
  return {
    toolId: tool.id,
    platformResourceId: tool.id,
    name: tool.name,
    version: tool.version || 'v1',
    language: 'go',
    sourceCode,
    sourceHash: '',
    sourceFiles: [{ path: 'main.go', content: sourceCode }],
    isTemplateSource: true,
    compileStatus: 'template',
    capsuleIds: tool.capsuleIds || [],
    resourceIds: tool.resourceIds || [],
    updatedAt: tool.metadata?.updatedAt || new Date().toISOString(),
  };
}

function templateToolSourceCode(): string {
  return `package main

import (
\t"encoding/json"
\t"io"
\t"log"
\t"os"
)

func main() {
\tinputBytes, err := io.ReadAll(os.Stdin)
\tif err != nil {
\t\tlog.Println("failed to read stdin:", err)
\t\tos.Exit(1)
\t}

\tvar input map[string]interface{}
\tif len(inputBytes) > 0 {
\t\tif err := json.Unmarshal(inputBytes, &input); err != nil {
\t\t\tlog.Println("failed to parse input JSON:", err)
\t\t\tos.Exit(1)
\t\t}
\t}

\toutput, err := process(input)
\tif err != nil {
\t\tlog.Println("tool failed:", err)
\t\tos.Exit(1)
\t}

\tif err := json.NewEncoder(os.Stdout).Encode(output); err != nil {
\t\tlog.Println("failed to write output:", err)
\t\tos.Exit(1)
\t}
}

func process(input map[string]interface{}) (map[string]interface{}, error) {
\toutput := map[string]interface{}{}
\t// TODO: implement business logic.
\treturn output, nil
}
`;
}

/**
 * Result of a synchronous source edit + recompile (Slice 16b).
 *
 * Compile failures are NOT thrown — they come back as a normal response
 * with `compileStatus: 'failed'` and `compileError` populated. The editor
 * renders the error inline next to the source. Only HTTP/transport
 * errors throw.
 *
 * Hash mismatches surface as a thrown Error; the editor should catch
 * those and prompt the user to refresh.
 */
export interface ToolSourceUpdateResult {
  toolId: string;
  platformResourceId: string;
  language: string;
  sourceHash: string;
  compileStatus: 'success' | 'failed';
  compileError?: string;
  binaryHash?: string;
  updatedAt: string;
}

export class ToolSourceConflictError extends Error {
  readonly status = 409;
  constructor(message: string) {
    super(message);
    this.name = 'ToolSourceConflictError';
  }
}

/**
 * Trigger a `.agion-tool` download for a tool.
 *
 * Slice 18. The platform proxies to the agent-host's export endpoint and
 * sets Content-Disposition; this helper just opens the URL in a new tab
 * (browser handles the download). Returns the URL so callers can also
 * surface a "copy link" button if they want.
 */
export function buildToolExportUrl(toolId: string): string {
  return `${API_URL}/v1/governance/tools/${toolId}/export`;
}

/**
 * Trigger an export download by anchor click.
 *
 * Same end result as buildToolExportUrl + window.open, but uses a
 * synthetic anchor with `download` so cookies (auth) ride along even if
 * the browser would otherwise treat the new-tab navigation as a top-level
 * navigation that loses some headers.
 */
export function downloadToolExport(toolId: string): void {
  const a = document.createElement('a');
  a.href = buildToolExportUrl(toolId);
  a.target = '_self';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Import a `.agion-tool` envelope into a pre-allocated tool record.
 *
 * The platform side has already created the tool resource (with host_id,
 * input/output schemas mirrored from the envelope, name, etc.); this
 * call asks the host to materialise the source + recompile.
 *
 * Compile failures come back as `compileStatus: 'failed'` with
 * `compileError` populated — same contract as putToolSource. The tool
 * still exists in the platform after a failed compile so the user can
 * fix it via the editor.
 */
export async function importToolEnvelope(
  toolId: string,
  envelope: unknown,
  missionId?: string,
): Promise<ToolSourceUpdateResult> {
  const response = await fetch(`${toolsBase(await resolveOwnerMission(toolId, missionId))}/${toolId}/import`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(envelope),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to import tool (HTTP ${response.status})`));
  }
  const data = await response.json();
  return {
    toolId: data.tool_id,
    platformResourceId: data.platform_resource_id,
    language: data.language,
    sourceHash: data.source_hash,
    compileStatus: data.compile_status,
    compileError: data.compile_error || undefined,
    binaryHash: data.binary_hash || undefined,
    updatedAt: data.updated_at,
  };
}

/**
 * Ask the host's LLM to rewrite the tool's source according to a
 * natural-language prompt — Phase 5 of TOOL-STUDIO-UNIFICATION.md.
 *
 * Returns the proposed new source for the caller to diff and let the
 * user accept. This call does NOT persist; on accept the caller plugs
 * the accepted source into putToolSource. That split keeps the user
 * as the final arbiter of LLM-driven changes (same shape as the
 * initial generate flow).
 *
 * Model selection follows generateAtHost: omit `modelId` to use the
 * organization's default code-generation model; pass an explicit ID
 * to override.
 */
export interface EditByPromptRequest {
  toolId: string;
  prompt: string;
  modelId?: string;
}

export interface EditByPromptResult {
  proposedSource: string;
}

export async function editToolByPrompt(
  req: EditByPromptRequest,
  missionId?: string,
): Promise<EditByPromptResult> {
  const response = await fetch(
    `${toolsBase(await resolveOwnerMission(req.toolId, missionId))}/${req.toolId}/edit-by-prompt`,
    {
      method: 'POST',
      headers: getHeaders(),
      credentials: 'include',
      body: JSON.stringify({
        edit_prompt: req.prompt,
        model_id: req.modelId,
      }),
    },
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Throw ToolServiceError so EditWithAIDialog can branch on the
    // machine-readable code/status (no-model CTA, timeout retry, host
    // offline) instead of flattening every failure into one opaque
    // message — that opacity is why Edit with AI "just fails".
    throw new ToolServiceError(
      extractErrorMessage(
        errorData,
        `Edit-by-prompt failed (HTTP ${response.status})`,
        response.status,
      ),
      { code: extractErrorCode(errorData), status: response.status },
    );
  }
  const data = await response.json();
  return {
    proposedSource: data.proposed_source ?? '',
  };
}

/**
 * Upload a `.agion-tool.zip` archive (or legacy `.agion-tool.json`) to a
 * pre-allocated tool record. The agent-host sniffs the format from the
 * file's magic bytes — both shapes are accepted.
 *
 * Companion to importToolEnvelope: that one takes a JSON object the
 * caller has already parsed; this one streams the raw file bytes
 * straight through, which is what a file-upload form always wants.
 */
export async function uploadToolImportFile(
  toolId: string,
  file: File,
): Promise<ToolSourceUpdateResult> {
  // Strip JSON-only headers so the upstream Content-Type is the file's
  // real type — the agent-host sniffs ZIPs by magic bytes regardless,
  // but a correct Content-Type keeps logs honest and helps middleware
  // along the way (e.g. CSRF checks that vary by content type).
  const baseHeaders = getHeaders() as Record<string, string>;
  const headers: Record<string, string> = {};
  for (const [k, v] of Object.entries(baseHeaders)) {
    if (k.toLowerCase() !== 'content-type') headers[k] = v;
  }
  headers['Content-Type'] = file.type || 'application/octet-stream';

  const response = await fetch(`${API_URL}/v1/governance/tools/${toolId}/import`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: file,
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to import tool file (HTTP ${response.status})`));
  }
  const data = await response.json();
  return {
    toolId: data.tool_id,
    platformResourceId: data.platform_resource_id,
    language: data.language,
    sourceHash: data.source_hash,
    compileStatus: data.compile_status,
    compileError: data.compile_error || undefined,
    binaryHash: data.binary_hash || undefined,
    updatedAt: data.updated_at,
  };
}

/**
 * Save and recompile a tool's source.
 *
 * Two shapes accepted:
 *   - { sourceCode } — single-file legacy path (wraps to main.go on the host)
 *   - { sourceFiles } — multi-file canonical shape (V26 / Slice 19)
 * When both are passed, sourceFiles wins (V26 canonical column).
 *
 * @param toolId Platform tool ID.
 * @param payload sourceCode OR sourceFiles plus the expected hash.
 *                expectedHash is the source_hash the editor saw when it
 *                loaded the source. Pass `''` (empty string) to force
 *                the save without optimistic concurrency. When non-empty
 *                and stale, throws ToolSourceConflictError.
 */
export interface PutToolSourcePayload {
  sourceCode?: string;
  sourceFiles?: ToolSourceFile[];
  expectedHash: string;
}

export async function putToolSource(
  toolId: string,
  payloadOrSource: PutToolSourcePayload | string,
  legacyExpectedHash?: string,
  missionId?: string,
): Promise<ToolSourceUpdateResult> {
  // Backwards-compat: callers that already pass (toolId, sourceCode, hash)
  // continue to work without code changes. New callers pass the payload
  // object.
  const payload: PutToolSourcePayload =
    typeof payloadOrSource === 'string'
      ? { sourceCode: payloadOrSource, expectedHash: legacyExpectedHash ?? '' }
      : payloadOrSource;

  const body: Record<string, unknown> = { expected_hash: payload.expectedHash };
  if (payload.sourceFiles && payload.sourceFiles.length > 0) {
    body.source_files = payload.sourceFiles.map((f) => ({ path: f.path, content: f.content }));
  } else if (payload.sourceCode !== undefined) {
    body.source_code = payload.sourceCode;
  } else {
    throw new Error('putToolSource requires either sourceCode or sourceFiles');
  }

  const owner = await resolveOwnerMission(toolId, missionId);
  const response = await fetch(`${toolsBase(owner)}/${toolId}/source`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (response.status === 409) {
    const errorData = await response.json().catch(() => ({}));
    throw new ToolSourceConflictError(
      extractErrorMessage(errorData, 'Source has changed on the host since you opened the editor'),
    );
  }
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to save source (HTTP ${response.status})`));
  }
  const data = await response.json();
  return {
    toolId: data.tool_id,
    platformResourceId: data.platform_resource_id,
    language: data.language,
    sourceHash: data.source_hash,
    compileStatus: data.compile_status,
    compileError: data.compile_error || undefined,
    binaryHash: data.binary_hash || undefined,
    updatedAt: data.updated_at,
  };
}

// ============================================================================
// Host-First Code Generation (NEW Architecture)
// ============================================================================

/**
 * Initiate code generation at host
 * This is the PRIMARY method for creating tools in the new architecture
 *
 * Flow:
 * 1. Creates tool reference in platform (hostId, schemas, governance only)
 * 2. Sends generation request to host via streaming client
 * 3. Host LLM generates Go code
 * 4. Host compiles and runs tests
 * 5. Returns status for SSE subscription
 *
 * @param request Tool specification including hostId and description
 * @returns Generation response with toolId and status
 */
export async function generateAtHost(request: GenerateAtHostRequest, missionId?: string): Promise<GenerateAtHostResponse> {
  // Sprint 1: include binding fields the caller derived from DAG edges
  // so the host generator grounds the LLM in real capsule/resource
  // shapes. Each field is conditionally included so an older backend
  // that doesn't know about them sees the legacy payload shape.
  const body: Record<string, unknown> = {
    host_id: request.hostId,
    name: request.name,
    description: request.description,
    category: request.category,
    input_schema: request.inputSchema,
    output_schema: request.outputSchema,
    model_id: request.modelId, // Pass selected model for code generation
  };
  if (request.pattern) body.pattern = request.pattern;
  if (request.capsuleIds?.length) body.capsule_ids = request.capsuleIds;
  if (request.streamIds?.length) body.stream_ids = request.streamIds;
  if (request.resourceIds?.length) body.resource_ids = request.resourceIds;
  if (request.capsuleContexts?.length) body.capsule_contexts = request.capsuleContexts;
  if (request.resourceContexts?.length) body.resource_contexts = request.resourceContexts;
  if (request.exampleInputs?.length) body.example_inputs = request.exampleInputs;
  if (request.missionId) body.mission_id = request.missionId;
  // Template mode: ask the host for a compilable no-op stub instead of LLM
  // code generation. Only sent when true so the legacy payload shape is
  // unchanged for normal generation.
  if (request.templateMode) body.template_mode = true;

  const response = await fetch(`${toolsBase(missionId)}/generate-at-host`, {
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ToolServiceError(
      extractErrorMessage(
        errorData,
        `Failed to initiate generation (HTTP ${response.status})`,
        response.status,
      ),
      { code: extractErrorCode(errorData), status: response.status },
    );
  }

  const data = await response.json();
  return {
    toolId: data.tool_id,
    hostId: data.host_id,
    status: data.status,
    message: data.message,
    compileStatus: data.compile_status,
    testsPassed: data.tests_passed,
    testsFailed: data.tests_failed,
    error: data.error,
  };
}

/**
 * Get current generation status from host
 * @param toolId Tool ID to check
 * @returns Current generation phase and progress
 */
export async function getHostGenerationStatus(toolId: string): Promise<HostGenerationStatus> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${toolId}/generation-status`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to get generation status (HTTP ${response.status})`));
  }

  const data = await response.json();
  return {
    toolId: data.tool_id,
    hostId: data.host_id,
    phase: data.phase,
    progress: data.progress,
    message: data.message,
    error: data.error,
    timestamp: data.timestamp,
  };
}

/**
 * Subscribe to host generation status updates via SSE
 *
 * @deprecated Use MissionStatusService.subscribeToolGenerationAsync() instead.
 * SSE was replaced by WebSocket due to APISIX 60s timeout killing connections.
 *
 * This function is kept for backwards compatibility but is no longer functional.
 * The SSE endpoint has been removed from governance-service.
 */
export function subscribeToHostGeneration(
  _toolId: string,
  _onStatus: (status: HostGenerationStatus) => void,
  _onComplete?: (status: HostGenerationStatus) => void,
  _onError?: (error: Event) => void
): { close: () => void } {
  console.warn(
    'subscribeToHostGeneration is deprecated. Use MissionStatusService.subscribeToolGenerationAsync() instead.'
  );
  // Return a no-op EventSource-like object
  return {
    close: () => {
      /* no-op */
    },
  };
}

/**
 * Set the owner mission for a tool.
 * Called when a tool created in ToolStudio is linked to a mission via "Save & Use".
 */
/**
 * @deprecated ADR-20260610: ownership is set AT CREATION (server-side) and
 * by adoption copies; in-place claiming via the legacy surface is rejected
 * (409). Kept only until the slice 7 cleanup; do not add callers.
 */
export async function setToolMissionOwner(toolId: string, missionId: string): Promise<void> {
  const response = await fetch(`${API_URL}/v1/governance/tools/${toolId}`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({ owner_mission_id: missionId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to set tool mission owner (HTTP ${response.status})`));
  }
}

// ============================================================================
// Tool Group Assignments (Issue #22)
// ============================================================================

export interface ToolGroupAssignment {
  groupName: string;
  assignedBy: string; // 'auto' | 'manual'
}

/**
 * Get current group assignments for a tool.
 */
export async function getToolGroupAssignments(toolName: string): Promise<ToolGroupAssignment[]> {
  const response = await fetch(`${API_URL}/v1/governance/tools/by-name/${encodeURIComponent(toolName)}/groups`, {
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
  });

  if (!response.ok) {
    if (response.status === 404) return [];
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to fetch tool groups (HTTP ${response.status})`));
  }

  const groupData = await response.json();
  return (groupData.groups || []).map((g: Record<string, unknown>) => ({
    groupName: g.group_name || g.groupName,
    assignedBy: g.assigned_by || g.assignedBy || 'auto',
  }));
}

/**
 * Replace all group assignments for a tool.
 */
export async function updateToolGroupAssignments(
  toolName: string,
  update: { groups: string[]; assignedBy: string }
): Promise<void> {
  const response = await fetch(`${API_URL}/v1/governance/tools/by-name/${encodeURIComponent(toolName)}/groups`, {
    method: 'PUT',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify({
      groups: update.groups,
      assigned_by: update.assignedBy,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(extractErrorMessage(errorData, `Failed to update tool groups (HTTP ${response.status})`));
  }
}
